<template>
  <div
    class="min-h-screen pb-4 grid grid-rows-[auto_1fr] bg-pitch-black text-total-white"
  >
    <div
      class="flex w-full justify-between items-center px-4 p-3 border border-x-0 border-t-0 border-b-1 border-prime-golden bg-pitch-black-900"
    >
      <a href="/" class="text-prime-golden font-avenir-medium"
        ><img
         class="w-24 h-24 -my-8 -ml-[10px]"

          src="@/assets/images/mason-logo.svg"
          alt="Mason Logo"
      /></a>
      <p class="text-red-500 font-medium cursor-pointer" @click="logout">Logout</p>
    </div>
    <div
      class="w-full pt-6 flex flex-col justify-start lg:justify-center items-start mx-auto max-w-md px-4"
    >
      <h1 class="text-4xl font-bold mt-4 mb-2">Verify Phone</h1>
      <div v-if="phoneNumberEntered">
        <p class="text-left text-total-white-500 mb-6">
          To keep your account secure, we need to verify your phone number. Enter the code
          we have sent to
          <strong>{{ phoneNumber ? phoneNumber : "your phone number" }}</strong> below.
        </p>
        <form class="w-full" @submit.prevent="onSubmit">
          <div class="mb-6">
            <label class="flex justify-start text-sm font-medium mb-1" for="code"
              >Code</label
            >
            <input
              id="code"
              v-model="code"
              type="text"
              maxlength="6"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
              placeholder="Enter verification code"
              aria-label="Verification code input"
            />
          </div>
          <div>
            <button
              type="submit"
              :disabled="isLoading"
              class="w-full py-2 rounded-full mb-4 bg-prime-golden text-black font-semibold text-lg focus:outline-none hover:bg-prime-golden-500 transition duration-300 ease-in-out"
            >
              <span v-if="isLoading">Verifying...</span>
              <span v-else>Verify</span>
            </button>
            <button
              @click.prevent="resendCode"
              :disabled="authStore.phoneCountdown > 0 || isLoading"
              class="w-full bg-dark-grey text-total-white p-3 rounded-full font-semibold"
              :class="{
                'opacity-30 cursor-not-allowed':
                  authStore.phoneCountdown > 0 || isLoading,
                'opacity-100': !(authStore.phoneCountdown > 0 || isLoading),
              }"
            >
              <span v-if="authStore.phoneCountdown > 0">
                Resend Code ({{ authStore.phoneCountdown }}s)
              </span>
              <span v-else>
                <span v-if="isLoading">Resending...</span>
                <span v-else>Resend Code</span>
              </span>
            </button>

            <p v-if="authStore.isCodeSent">
              You can resend the code in {{ authStore.countdown }} seconds.
            </p>
          </div>
          <!-- Display error message -->
          <div v-if="errorMessage" class="text-red-500 mt-2">
            {{ errorMessage }}
          </div>
          <!-- Display success message -->
          <div v-if="successMessage" class="text-green-500 mt-2">
            {{ successMessage }}
          </div>
        </form>
      </div>
      <div v-else class="w-full">
        <div class="my-6 w-full">
          <label class="flex justify-start text-md font-medium mb-4" for="phone"
            >Please enter your phone number</label
          >
          <PhoneInput id="phone" v-model="phoneNumber" />
        </div>
        <div>
          <button
            @click.prevent="linkPhoneNumber"
            :disabled="!phoneNumber || isLoading"
            class="w-full py-2 rounded-full mb-4 bg-prime-golden text-black font-semibold text-lg focus:outline-none hover:bg-prime-golden-500 transition duration-300 ease-in-out"
          >
            {{ isLoading ? "Please wait..." : "Send Verification Code" }}
          </button>
        </div>
        <!-- Display error message -->
        <div v-if="errorMessage" class="text-red-500 mt-2">
          {{ errorMessage }}
        </div>
      </div>

      <!-- Footer -->
      <div class="text-center w-full mt-8 text-total-white-500 text-sm">
        <a href="#" class="hover:text-prime-golden">Terms of Service</a>
        <span class="mx-2">|</span>
        <a href="https://www.mason.tech/privacy-policy" class="hover:text-prime-golden">Privacy Policy</a>
        <p class="mt-4">&copy; 2024 Mason Intelligence, Inc.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { useRouter } from "vue-router";
import PhoneInput from "@/components/PhoneInput.vue";

const code = ref("");
const phoneNumberEntered = ref(false);
const errorMessage = ref("");
const successMessage = ref(""); // New success message ref
const isLoading = ref(false); // Loading state
const authStore = useAuthStore();
const userStore = useUserStore();
const router = useRouter();

// Computed property to get the user's phone number
const phoneNumber = ref("");

// Function to handle form submission
const onSubmit = async () => {
  if (!code.value) {
    errorMessage.value = "Please enter the verification code.";
    return;
  }

  isLoading.value = true; // Set loading state to true
  try {
    await authStore.verifyPhoneNumber(phoneNumber.value.replace(/\s/g, ""), code.value);
    authStore.verificationAndAuthorizationValuesExist = false;
    errorMessage.value = "";
    successMessage.value =
      "Phone number verified successfully! Please wait to be redirected.";
    setTimeout(() => {
      router.push({ name: "ProjectsPage" });
    }, 3000);
  } catch (error) {
    // Extract and show only the 'msg' field from the 'detail' array if it exists
    if (Array.isArray(error.response?.data?.detail)) {
      errorMessage.value = error.response.data.detail.map((item) => item.msg).join(", ");
    } else {
      errorMessage.value = "Failed to verify phone number. Please try again.";
    }
  } finally {
    isLoading.value = false;
  }
};

const linkPhoneNumber = async () => {
  errorMessage.value = "";
  try {
    await userStore.updateUserProfile({
      phone_number: phoneNumber.value.replace(/\s/g, ""),
    });
    //successMessage.value = "Phone number updated successfully!";
    try {
      await authStore.requestSmsVerificationCode(phoneNumber.value.replace(/\s/g, ""));
    } catch (error) {
      errorMessage.value = "Unable to send verification code";
    }

    phoneNumberEntered.value = true;
  } catch (error) {
    errorMessage.value = "Failed to update phone number. Please try again.";
  }
};

// Function to resend the verification code
const resendCode = async () => {
  if (authStore.phoneCountdown > 0) return; // Prevent resending during countdown

  isLoading.value = true;
  try {
    console.log("Resending code to:", phoneNumber.value.replace(/\s/g, ""));
    await authStore.requestSmsVerificationCode(phoneNumber.value.replace(/\s/g, ""));
    successMessage.value = "Verification code has been resent to your phone.";
    errorMessage.value = ""; // Clear previous error message
  } catch (error) {
    if (Array.isArray(error.response?.data?.detail)) {
      errorMessage.value = error.response.data.detail.map((item) => item.msg).join(", ");
    } else {
      errorMessage.value = "Failed to resend code. Please try again.";
    }
  } finally {
    isLoading.value = false;
  }
};

// Fetch the user's profile and trigger code sending on component mount
onMounted(async () => {
  try {
    await userStore.fetchUserProfile();
    // Automatically trigger sending the verification code
    if (authStore.isPhoneVerified) {
      router.push({ name: "ProjectsPage" });
    } else {
      console.log("Phone number not verified yet: ", authStore);
      console.log("!!", authStore.isPhoneVerified);
    }
  } catch (error) {
    const errorMessage = "Unable to load user information.";
    console.log("error:", errorMessage + error);
  }
});

const logout = async () => {
  await authStore.logout();
  router.push({ name: "LogIn" });
};
</script>

<style scoped>
/* Custom styles, if needed */
</style>
